import React, { useState, useEffect, useContext } from 'react';
import { fetchApps as fetchIntegrations } from '../api/Api';
import { useSelector } from 'react-redux';
import NotificationContext from '../../../providers/NotificationProvider';
import { showSuccessToast, showErrorToast } from '../../../components/commonComponents/gxAntdToast/toastUtils';
import GoogleCalendarIntegration from '../components/GoogleCalendarIntegration';

const Integrations = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGoogleCalendarModalOpen, setIsGoogleCalendarModalOpen] = useState(false);
  const { currentBrand } = useSelector((state) => state?.app);

  const notification = useContext(NotificationContext);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      setLoading(true);
      const response = await fetchIntegrations(currentBrand?.brand_id);
      const { integrations } = response.data;
      
      const formattedApps = integrations.map(integration => ({
        id: integration.id,
        name: integration.name,
        code: integration.code,
        category: integration.category || 'Integration',
        description: integration.description,
        icon: integration.settings?.icon_url || '🔌',
        type: 'free',
        hasVideo: false,
        connected: integration.brand_connected || integration.user_connected,
        status: integration.status,
        features: integration.settings?.features || [],
        docsUrl: integration.settings?.docs_url,
        manifest: integration.manifest,
        connectionScope: integration.connection_scope,
        stats: integration.stats
      }));
      
      setApps(formattedApps);
    } catch (err) {
      console.error('Error fetching integrations:', err);
      showErrorToast(notification, err?.message || err?.response?.data?.message || err?.response?.data?.error || 'Failed to load integrations. Please try again later.');
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (app) => {
    setSelectedApp(app);
    
    // Check if it's Google Calendar integration
    if (app.code === 'google_calendar' || app.name.toLowerCase().includes('google calendar')) {
      setIsGoogleCalendarModalOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleOAuthConnect = () => {
    if (selectedApp?.manifest?.oauth?.start_endpoint) {
      const startUrl = selectedApp.manifest.oauth.start_endpoint;
      window.location.href = startUrl;
    }
  };

  const handleGoogleCalendarClose = () => {
    setIsGoogleCalendarModalOpen(false);
    setSelectedApp(null);
  };

  const handleConnectionSuccess = () => {
    // Refresh the apps list to get updated connection status
    fetchApps();
    showSuccessToast(notification, 'Integration connected successfully!');
  };

  const filteredApps = apps.filter(app => {
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'free' && app.type === 'free') || 
      (activeTab === 'paid' && app.type === 'paid');
    
    const matchesCategory = selectedCategory === 'all' || app.category === selectedCategory;
    
    return matchesTab && matchesCategory;
  });

  const ConnectModal = () => {
    if (!isModalOpen || !selectedApp) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-gray-200">
                {selectedApp.icon.startsWith('http') ? (
                  <img src={selectedApp.icon} alt={selectedApp.name} className="w-12 h-12 object-contain" />
                ) : (
                  <span className="text-3xl">{selectedApp.icon}</span>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">{selectedApp.name}</h2>
                <p className="text-sm text-gray-500 mt-1 capitalize">{selectedApp.category}</p>
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Description */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">About this integration</h3>
              <p className="text-gray-600 leading-relaxed">{selectedApp.description}</p>
            </div>

            {/* Features */}
            {selectedApp.features.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Features</h3>
                <div className="grid grid-cols-1 gap-2">
                  {selectedApp.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-gray-700 capitalize">
                        {feature.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Connection Info */}
            {selectedApp.connected && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-emerald-800">
                    Already connected
                  </span>
                </div>
                {selectedApp.stats?.last_connected_at && (
                  <p className="text-xs text-emerald-600 mt-1 ml-7">
                    Last connected: {new Date(selectedApp.stats.last_connected_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}

            {/* Permissions Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Permissions Required</h4>
              <p className="text-xs text-blue-700">
                By connecting, you'll authorize GainWix to access your {selectedApp.name} account. 
                You can revoke access at any time from your settings.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end gap-3">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleOAuthConnect}
              disabled={selectedApp.connected}
              className={`px-6 py-2.5 font-medium rounded-lg transition-colors flex items-center gap-2 ${
                selectedApp.connected
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
            >
              {selectedApp.icon.startsWith('http') && (
                <img src={selectedApp.icon} alt="" className="w-5 h-5" />
              )}
              {selectedApp.connected ? 'Already Connected' : `Connect with ${selectedApp.name.split(' ')[0]}`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AppCard = ({ app }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col h-full hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200">
            {app.icon.startsWith('http') ? (
              <img src={app.icon} alt={app.name} className="w-8 h-8 object-contain" />
            ) : (
              <span className="text-2xl">{app.icon}</span>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">{app.name}</h3>
            <p className="text-sm text-gray-500 capitalize">{app.category}</p>
          </div>
        </div>
        {app.connected && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-50 border border-emerald-200">
            <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-medium text-emerald-700">Connected</span>
          </div>
        )}
      </div>
      
      <p className="text-sm text-gray-600 mb-4 flex-grow line-clamp-2">
        {app.description}
      </p>
      
      <button
        onClick={() => handleConnect(app)}
        className={`w-full font-medium py-3 rounded-lg transition-colors ${
          app.connected
            ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
            : 'bg-emerald-600 text-white hover:bg-emerald-700'
        }`}
      >
        {app.connected ? 'Manage Connection' : 'Connect'}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <div className="text-gray-500">Loading integrations...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-semibold text-gray-900">Integrations</h1>
            </div>
            <p className="text-gray-600">Connect GainWix with various applications</p>
          </div>

          {/* Tabs and Filter */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div className="flex gap-6 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('all')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'all'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All Apps
              </button>
              <button
                onClick={() => setActiveTab('free')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'free'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Free Apps
              </button>
              <button
                onClick={() => setActiveTab('paid')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'paid'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Paid Apps
              </button>
            </div>
          </div>

          {/* Apps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredApps.map(app => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>

          {/* Empty State */}
          {filteredApps.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500">No integrations found</p>
            </div>
          )}
        </div>
      </div>

      {/* Connect Modal */}
      <ConnectModal />

      {/* Google Calendar Modal */}
      <GoogleCalendarIntegration
        app={selectedApp}
        isOpen={isGoogleCalendarModalOpen}
        onClose={handleGoogleCalendarClose}
        onConnectionSuccess={handleConnectionSuccess}
      />
    </>
  );
};

export default Integrations;